import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LockIcon from "@mui/icons-material/Lock";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import { hubApi } from "../../services/hubApi";

// ── Aba Bot Token (original) ──
function BotTokenTab({ onSuccess }) {
  const [botToken, setBotToken] = useState("");
  const [botName, setBotName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const isValidToken = /^\d+:[A-Za-z0-9_-]{35,}$/.test(botToken.trim());

  const handleConnect = async () => {
    if (!isValidToken) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await hubApi.telegramConnect(botToken.trim(), botName || undefined);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao conectar bot.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Bot conectado!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          @{result.botUsername} ({result.botFirstName})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {result.webhookConfigured
            ? "Webhook configurado automaticamente"
            : "Modo polling ativo (desenvolvimento)"}
        </Typography>
        <Button
          variant="contained"
          onClick={onSuccess}
          sx={{
            mt: 3,
            background: "linear-gradient(135deg, #6B48FF 0%, #8B6FFF 100%)",
          }}
        >
          Concluir
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {/* Instrucoes */}
      <Box sx={{ flex: "1 1 280px" }}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToyIcon sx={{ color: "#0088CC" }} />
          Como obter o token
        </Typography>

        <List dense sx={{ mb: 2 }}>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LooksOneIcon sx={{ color: "primary.light" }} />
            </ListItemIcon>
            <ListItemText
              primary="Abra o Telegram e busque @BotFather"
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LooksTwoIcon sx={{ color: "primary.light" }} />
            </ListItemIcon>
            <ListItemText
              primary='Envie /newbot e siga as instrucoes'
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItem>
          <ListItem sx={{ px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Looks3Icon sx={{ color: "primary.light" }} />
            </ListItemIcon>
            <ListItemText
              primary="Copie o token e cole aqui"
              primaryTypographyProps={{ variant: "body2" }}
            />
          </ListItem>
        </List>

        <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
          O token tem o formato: 123456789:ABCDEFGhijklmnopqrstuvwxyz...
        </Alert>
      </Box>

      {/* Formulario */}
      <Box sx={{ flex: "1 1 320px" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Token do Bot"
          placeholder="123456789:ABCDEFGhijklmnop..."
          value={botToken}
          onChange={(e) => setBotToken(e.target.value)}
          error={botToken.length > 0 && !isValidToken}
          helperText={
            botToken.length > 0 && !isValidToken ? "Formato invalido" : " "
          }
          autoComplete="off"
          inputProps={{ autoComplete: "off" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SmartToyIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Nome da Conexao (opcional)"
          placeholder="Meu Bot de Atendimento"
          value={botName}
          onChange={(e) => setBotName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleConnect}
          disabled={loading || !isValidToken}
          sx={{
            py: 1.5,
            background: "linear-gradient(135deg, #0088CC 0%, #006699 100%)",
            "&:hover": { opacity: 0.9 },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Conectar Bot"
          )}
        </Button>
      </Box>
    </Box>
  );
}

// ── Aba Numero Pessoal (MTProto) ──
function PhoneNumberTab({ onSuccess }) {
  const [step, setStep] = useState(0); // 0=phone, 1=code, 2=2fa, 3=done
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneName, setPhoneName] = useState("");
  const [code, setCode] = useState("");
  const [password2fa, setPassword2fa] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [connectionId, setConnectionId] = useState(null);
  const [phoneCodeHash, setPhoneCodeHash] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  const isValidPhone = /^\+?\d{8,15}$/.test(phoneNumber.replace(/[\s()-]/g, ""));
  const isValidCode = /^\d{4,8}$/.test(code.trim());

  // Etapa 1: Enviar codigo
  const handleSendCode = async () => {
    if (!isValidPhone) return;
    setError("");
    setLoading(true);
    try {
      const phone = phoneNumber.replace(/[\s()-]/g, "");
      const { data } = await hubApi.telegramPhoneConnect(
        phone.startsWith("+") ? phone : `+${phone}`,
        phoneName || undefined
      );
      setConnectionId(data.connectionId);
      setPhoneCodeHash(data.phoneCodeHash);
      setStep(1);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao enviar codigo.");
    } finally {
      setLoading(false);
    }
  };

  // Etapa 2: Verificar codigo OTP
  const handleVerifyCode = async () => {
    if (!isValidCode) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await hubApi.telegramVerifyCode(
        connectionId,
        code.trim(),
        phoneCodeHash
      );
      if (data.status === "need_2fa") {
        setStep(2);
      } else {
        setUserInfo(data);
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao verificar codigo.");
    } finally {
      setLoading(false);
    }
  };

  // Etapa 3: Verificar 2FA
  const handleVerify2FA = async () => {
    if (!password2fa) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await hubApi.telegramVerify2FA(connectionId, password2fa);
      setUserInfo(data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || "Erro ao verificar senha 2FA.");
    } finally {
      setLoading(false);
    }
  };

  // Tela de sucesso
  if (step === 3) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: "success.main", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Telegram conectado!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Conta pessoal vinculada com sucesso
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Todas as mensagens recebidas no Telegram serao encaminhadas para o atendimento.
        </Typography>
        <Button
          variant="contained"
          onClick={onSuccess}
          sx={{
            mt: 3,
            background: "linear-gradient(135deg, #6B48FF 0%, #8B6FFF 100%)",
          }}
        >
          Concluir
        </Button>
      </Box>
    );
  }

  const stepLabels = ["Numero", "Codigo", "Conectado"];
  if (step === 2) stepLabels.splice(2, 0, "2FA");

  return (
    <Box>
      {/* Stepper */}
      <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
        {stepLabels.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Etapa 0: Numero de telefone */}
      {step === 0 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3, fontSize: "0.85rem" }}>
            <strong>Conexao via numero pessoal</strong> — Ao conectar seu numero,
            todas as mensagens recebidas no Telegram serao encaminhadas para o
            sistema de atendimento. Use o formato internacional (ex: +5551999999999).
          </Alert>

          <TextField
            fullWidth
            label="Numero de Telefone"
            placeholder="+5551999999999"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            error={phoneNumber.length > 5 && !isValidPhone}
            helperText={
              phoneNumber.length > 5 && !isValidPhone
                ? "Use o formato internacional: +5551999999999"
                : "Inclua o codigo do pais (ex: +55 para Brasil)"
            }
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneAndroidIcon sx={{ color: "#0088CC" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Nome da Conexao (opcional)"
            placeholder="Meu Telegram Pessoal"
            value={phoneName}
            onChange={(e) => setPhoneName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSendCode}
            disabled={loading || !isValidPhone}
            sx={{
              py: 1.5,
              background: "linear-gradient(135deg, #0088CC 0%, #006699 100%)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Enviar Codigo"
            )}
          </Button>
        </Box>
      )}

      {/* Etapa 1: Codigo OTP */}
      {step === 1 && (
        <Box>
          <Alert severity="success" sx={{ mb: 3, fontSize: "0.85rem" }}>
            Codigo enviado! Verifique seu aplicativo Telegram. O codigo aparecera
            como mensagem do Telegram ou como notificacao.
          </Alert>

          <TextField
            fullWidth
            label="Codigo de Verificacao"
            placeholder="12345"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            error={code.length > 0 && !isValidCode}
            helperText={
              code.length > 0 && !isValidCode
                ? "O codigo deve ter entre 4 e 8 digitos"
                : "Digite o codigo recebido no Telegram"
            }
            autoComplete="one-time-code"
            inputProps={{
              maxLength: 8,
              inputMode: "numeric",
              style: {
                fontSize: "1.5rem",
                letterSpacing: "0.5em",
                textAlign: "center",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: "#0088CC" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setStep(0);
                setCode("");
                setError("");
              }}
              sx={{ flex: 1 }}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleVerifyCode}
              disabled={loading || !isValidCode}
              sx={{
                flex: 2,
                py: 1.5,
                background: "linear-gradient(135deg, #0088CC 0%, #006699 100%)",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Verificar Codigo"
              )}
            </Button>
          </Box>
        </Box>
      )}

      {/* Etapa 2: Senha 2FA */}
      {step === 2 && (
        <Box>
          <Alert severity="warning" sx={{ mb: 3, fontSize: "0.85rem" }}>
            <strong>Verificacao em duas etapas</strong> — Sua conta tem senha 2FA
            habilitada. Digite sua senha do Telegram (cloud password) para
            completar a conexao.
          </Alert>

          <TextField
            fullWidth
            type="password"
            label="Senha 2FA do Telegram"
            placeholder="Sua senha de verificacao em duas etapas"
            value={password2fa}
            onChange={(e) => setPassword2fa(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: "#ff9800" }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setStep(1);
                setPassword2fa("");
                setError("");
              }}
              sx={{ flex: 1 }}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleVerify2FA}
              disabled={loading || !password2fa}
              sx={{
                flex: 2,
                py: 1.5,
                background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Verificar Senha"
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Componente Principal ──
export default function TelegramConnect({ onBack, onSuccess }) {
  const [tabIndex, setTabIndex] = useState(0);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2, color: "text.secondary" }}
      >
        Voltar
      </Button>

      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
          },
        }}
      >
        <Tab
          icon={<SmartToyIcon sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Bot Token"
        />
        <Tab
          icon={<PhoneAndroidIcon sx={{ fontSize: 20 }} />}
          iconPosition="start"
          label="Numero Pessoal"
        />
      </Tabs>

      {tabIndex === 0 && <BotTokenTab onSuccess={onSuccess} />}
      {tabIndex === 1 && <PhoneNumberTab onSuccess={onSuccess} />}
    </Box>
  );
}
